import React, { useEffect, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import ReactTable from 'react-table';
import Loader from '../common/loader';
import api from '../../config/axios';
import { date, time, dateQuery } from '../../utils/date';
import miscUtils from '../../utils/miscUtils';
import { useStateValue } from '../../store';
import { getTimeFormat, getShortBottlesPurchase } from './utils';
import OrderItem from './common/order-item';

const { currency } = miscUtils;

const columnsDataTable = (count, role) => [
    {
        Header: role !== 'DELIVERY' ? <b>{`${count} pedidos`}</b> : null,
        id: 'delete',
        accessor: (str) => 'delete',
        Cell: (row) => <OrderItem data={row.original} />,
        style: {
            textAlign: 'center',
            width: '100%',
        },
        sortable: false,
        width: '100%',
    },
];

const arrayFilter = [
    {
        label: 'Últimos 7 días',
        value: 'week',
    },
    {
        label: 'Este mes',
        value: 'currently-month',
    },
    {
        label: 'Mes pasado',
        value: 'last-month',
    },
];

const getDatesRange = (type) => {
    let dates = { startDate: null, endDate: null };
    const startDate = new Date();
    const endDate = new Date();

    switch (type) {
        case 'week':
            endDate.setDate(endDate.getDate() + 1);
            dates.endDate = dateQuery(endDate);
            startDate.setDate(startDate.getDate() - 7);
            dates.startDate = dateQuery(startDate);
            break;
        case 'currently-month':
            dates.startDate = dateQuery(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
            dates.endDate = dateQuery(new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0));
            break;
        case 'last-month':
            dates.startDate = dateQuery(
                new Date(startDate.getFullYear(), startDate.getMonth() - 1, 1),
            );
            dates.endDate = dateQuery(new Date(endDate.getFullYear(), endDate.getMonth(), 0));
            break;
    }
    return dates;
};

const getLastTransaction = (transactions) =>
    transactions.reduce((a, b) => (a.createdAt > b.createdAt ? a : b));

const getFirstAndLastDateOfTheWeek = (user) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dayOfWeek = today.getDay(); // 0=Dom, 1=Lun, ..., 6=Sáb

    // Días a restar para llegar al sábado anterior (o hoy si es sábado)
    // Sáb=6 → 0, Dom=0 → 1, Lun=1 → 2, Mar=2 → 3, Mié=3 → 4, Jue=4 → 5, Vie=5 → 6
    const daysToSaturday = dayOfWeek === 6 ? 0 : dayOfWeek + 1;

    const first = new Date(today);
    first.setDate(today.getDate() - daysToSaturday);

    const last = new Date(first);
    last.setDate(first.getDate() + 6); // Sábado + 6 días = Viernes

    var lunes = new Date(today.setDate(today.getDate() - today.getDay() + 1)); // lunes
    var sabado = new Date(today.setDate(lunes.getDate() + 6)); // sábado

    if (user.franchiseId === '40b94417-6af0-4ebd-b6f3-9d962e47b7dc') { // colon (por ahora)
        // lunes a sabado
        return {
            first: lunes,
            last: sabado,
        };
    }

    // sabado a viernes
    return { first, last };
};


function Orders(props) {
    const [{ auth }] = useStateValue();
    const [loaded, setLoaded] = useState(false);
    const [orders, setOrders] = useState([]);
    const [productsDelivered, setProductsDelivered] = useState(null);
    const [pagination, setPagination] = useState({ page: 1 });
    const [pageTable, setPageTable] = useState(0);
    const [filterOrders, setFilterOrders] = useState('week');
    const [deliveryUsers, setDeliveryUsers] = useState([]);
    const [showStatitics, setShowStatitics] = useState(false);
    const { first: mondayWeek, last: sundayWeek } = getFirstAndLastDateOfTheWeek(auth.user);

    //const { first: mondayWeek, last: sundayWeek } = getFirstAndLastDateOfTheWeek();

    useEffect(() => {
        setLoaded(false);
        const { startDate, endDate } = getDatesRange(filterOrders);
        Promise.all([
            fetchOrders(pagination.page, startDate, endDate),
            fetchDeliveredProducts(
                mondayWeek.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
                sundayWeek.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
                auth.user.franchiseId || '5e95f51f-5b66-4c58-b2af-7a54e8b1df1e',
            ),
        ]).then((values) => {
            setProductsDelivered(values[1]);
            setLoaded(true);
        });
    }, [filterOrders]);

    const fetchOrders = async (page, startDate, endDate) => {
        const { data } = await api.get(
            `purchase/franchise?page=${page}&startDate=${startDate}&endDate=${endDate}`,
        );
        let orders = null;

        if (auth.user.role === 'DELIVERY') {
            // mostrar solo las ordenes que han sido creadas y han sido enviadas
            orders = parseToDatatable(
                data.data.rows.filter((o) => {
                    const lastTransaction = getLastTransaction(o.transactions);
                    if (
                        lastTransaction.name === 'Pedido Creado' ||
                        lastTransaction.name === 'Pedido Enviado' ||
                        lastTransaction.name === 'Pago Confirmado' ||
                        o.userId === auth.user.id
                    ) {
                        return true;
                    }
                    return false;
                }),
            );
        } else {
            // mostrar todas las ordenes (creadas, enviadas y entregadas)
            orders = parseToDatatable(data.data.rows);
        }

        const dataPagination = {
            ...data.meta.pagination,
            total: data.data.count,
        };
        setOrders(orders);
        setPagination(dataPagination);
        setPageTable(data.meta.pagination.page - 1);

        // mostrar el listado de usuarios deliveries
        // en el Select de deliveries
        handleDeliveryUsers(data.data.rows);
        return;
    };

    const fetchDeliveredProducts = async (startDate, endDate, franchiseId) => {
        try {
            const { data } = await api.get(
                `purchase/per-delivery?startDate=${startDate}&endDate=${endDate}&franchiseId=${franchiseId}`,
            );
            return data.data;
        } catch (e) {
            throw new Error(e);
        }
    };

    const handleDeliveryUsers = (array) => {
        const arrayOfUsersRepetead = array.filter((f) => f.user).map((m) => m.user);

        // se eliminan los valores repetidos del array
        // se hace match por el id para manejar el array de objetos
        const arrayOfUsers = arrayOfUsersRepetead.filter(
            (value, index, self) =>
                index === self.findIndex((t) => t.id === value.id && t.name === value.name),
        );
        setDeliveryUsers(arrayOfUsers);
    };

    const parseToDatatable = (array) => {
        const parsed = array.map((item) => {
            const lastTransaction = item.transactions.reduce((a, b) =>
                a.createdAt > b.createdAt ? a : b,
            );
            const firstTransaction = item.transactions.reduce((a, b) =>
                b.createdAt > a.createdAt ? a : b,
            );
            const productRecargas = item.products.find((item) => item.name === 'Recarga');
            const quantityOrder = productRecargas
                ? getShortBottlesPurchase(
                    productRecargas.purchaseProducts.quantity,
                    item.address.newBottles,
                )
                : '';

            // bandera para mostrar a primera hora
            const hourPurchase = new Date(firstTransaction.createdAt).getHours();
            let firstMomentNextDate = false;

            const purchaseWeekday = new Date(item.createdAt).getDay();
            const deliveryWeekday = item.delivery.weekday;

            // delivery nuevo (schedule)
            if (deliveryWeekday) {
                // en lugar de 8, hay que colocar el startTime del delivery
                if (purchaseWeekday === deliveryWeekday && hourPurchase < 8) {
                    firstMomentNextDate = true;
                } else if (purchaseWeekday !== deliveryWeekday) {
                    firstMomentNextDate = true;
                }
            } else {
                // delivery viejo
                // horario normal
                if (hourPurchase > 8 && hourPurchase < 18) {
                    // no se hace nada
                } else {
                    // horario fuera del habitual
                    firstMomentNextDate = true;
                }
            }

            const itemFormatted = {
                id: item.id,
                estado: renderBadge(lastTransaction.name),
                carreraLarga: item.address.longway ? renderLongway() : null,
                clienteNuevo: item.oldPurchases.length === 1 ? badgeNewClient() : null,
                direccionNueva:
                    item.oldPurchases.length > 1 && item.purchasesByAddress.length === 1
                        ? badgeNewAddress()
                        : null,
                entrega: `${item.delivery_time
                    ? getTimeFormat(item.delivery_time)
                    : firstMomentNextDate
                        ? `8:00 am - Express`
                        : `${time(firstTransaction.createdAt)} - Express`
                    }`,
                delivery_time: item.delivery_time,
                vueltos: item.changeCash,
                'fecha de pedido': `${date(item.createdAt)} ${time(firstTransaction.createdAt)}`,
                cantidad: miscUtils.renderTextPedido(item),
                hasOnlyRecargas: miscUtils.hasOnlyRecargas(item),
                quantityDescription: quantityOrder,
                cliente: item.client.name,
                direccion: item.address ? item.address.street1 : item.addressQuick,
                gpsVerified: item.address.gpsVerified,
                user: item.user,
                paymentMethod: item.paymentMethod.name,
                observation: purchaseWithNotes(item.purchasesByAddress.slice(0, 2)),
                observationImage: purchaseWithImage(item.purchasesByAddress.slice(0, 2)),
                couponId: item.couponId,
                observationBotellon: item.address.observation,
                isPhotoBottle: !!item.address.photoBottle,
                subscription: item.subscriptionId,
                preNote: item.preNote,
                requestedFrom: item.requestedFrom,
                payment_confirmation_id: item.payment_confirmation_id,
                addressInfo: item.address,
            };

            return itemFormatted;
        });
        return parsed;
    };

    const renderBadge = (status) => {
        switch (status) {
            case 'Pedido Creado':
                return <span className="badge badge-warning">Por enviar</span>;
            case 'Pedido Cancelado':
                return <span className="badge badge-danger">Cancelado</span>;
            case 'Pedido Enviado':
                return <span className="badge badge-info">Enviado</span>;
            case 'Pedido Recibido':
                return <span className="badge badge-success">Recibido</span>;
            case 'Pedido Entregado':
                return <span className="badge badge-success">Entregado</span>;
            case 'Pedido Rechazado':
                return <span className="badge badge-danger">Rechazado</span>;
            case 'Pago Confirmado':
                return <span className="badge badge-warning">Pago Confirmado</span>;
            default:
                return null;
        }
    };

    const renderLongway = () => <span className="badge badge-info">Larga</span>;

    const badgeNewClient = () => <span className="badge badge-danger">Nuevo Cliente</span>;

    const badgeNewAddress = () => <span className="badge badge-danger">Nueva Direccion</span>;

    const handlePage = async (page) => {
        setLoaded(false);
        const { startDate, endDate } = getDatesRange(filterOrders);
        await fetchOrders(page, startDate, endDate);
        setLoaded(true);
    };

    const refreshPage = async () => {
        setLoaded(false);
        const { startDate, endDate } = getDatesRange(filterOrders);
        await fetchOrders(0, startDate, endDate);
        setLoaded(true);
    };

    const quantityDeliveredBottlesByDelivery = () => {
        let quantityByUserDelivery = 0;
        if (productsDelivered.length === 0) {
            quantityByUserDelivery = {
                quantity: 0,
                longway: 0
            }
            return quantityByUserDelivery
        }
        if (auth.user.franchiseId && auth.user.role !== 'FRANCHISE') {
            const userDeliveredOrder = productsDelivered.find(
                (products) => products.userId === auth.user.id,
            );
            if (!!userDeliveredOrder) {
                quantityByUserDelivery = productsDelivered.find(
                    (products) => products.userId === auth.user.id,
                ).quantity;
                quantityByUserDelivery = {
                    quantity: userDeliveredOrder.quantity,
                    longway: userDeliveredOrder.longway_count
                }
            } else {
                quantityByUserDelivery = {
                    quantity: 0,
                    longway: 0
                }
            }
        } else {
            const totalQuantity = productsDelivered
                .map((e) => e.quantity)
                .reduce((a, b) => Number(a) + Number(b));

            const totalLongwayQuantity = productsDelivered
                .map((e) => e.longway_count)
                .reduce((a, b) => Number(a) + Number(b));

            quantityByUserDelivery = {
                quantity: totalQuantity,
                longway: totalLongwayQuantity
            }
        }

        return quantityByUserDelivery;
    };

    const totalPages = () => Math.ceil(pagination.total / pagination.length);

    const purchaseWithNotes = (lastPurchases) => {
        // Validar si lastPurchases existe y tiene elementos
        if (!lastPurchases || lastPurchases.length === 0) {
            return null;
        }

        // Si solo hay una compra
        if (lastPurchases.length === 1) {
            const purchase = lastPurchases[0];
            const hasObservation = purchase.transactions.find(
                (transaction) => !!transaction.observation,
            );
            return hasObservation ? hasObservation.observation : null;
        }

        // Si hay dos compras (caso original)
        if (lastPurchases.length === 2) {
            // Ordenar lastPurchases por id para identificar cuál es más reciente
            const sortedOldPurchases = lastPurchases.sort((a, b) => a.id - b.id);
            const oldestPurchase = sortedOldPurchases[0];
            const newestPurchase = sortedOldPurchases[1];

            // Verificar si cada purchase tiene observación
            const oldestHasObservation = oldestPurchase.transactions.find(
                (transaction) => !!transaction.observation,
            );
            const newestHasObservation = newestPurchase.transactions.find(
                (transaction) => !!transaction.observation,
            );

            // Caso 1: Solo la más reciente tiene observación - retornar el texto
            if (newestHasObservation) {
                return newestHasObservation.observation;
            }

            // Caso 2: La más antigua tiene observación y la más reciente no ha sido entregada - retornar la observación de la más antigua
            if (
                oldestHasObservation &&
                !newestPurchase.transactions.find(
                    (transaction) => transaction.name === 'Pedido Entregado',
                )
            ) {
                return oldestHasObservation.observation;
            }

            // Caso 3: Solo la más antigua tiene observación - retornar null
            if (oldestHasObservation) {
                return null;
            }

            // Caso 4: Ninguna tiene observación - retornar null
            return null;
        }
    };

    const purchaseWithImage = (lastPurchases) => {
        if (!lastPurchases || lastPurchases.length === 0) {
            return null;
        }
        if (lastPurchases.length === 1) {
            return lastPurchases[0].imageObservation;
        }

        // Si hay dos compras (caso original)
        if (lastPurchases.length === 2) {
            // Ordenar lastPurchases por id para identificar cuál es más reciente
            const sortedOldPurchases = lastPurchases.sort((a, b) => a.id - b.id);
            const oldestPurchase = sortedOldPurchases[0];
            const newestPurchase = sortedOldPurchases[1];

            // Verificar si cada purchase tiene observación
            const oldestHasObservation = oldestPurchase.transactions.find(
                (transaction) => !!transaction.imageObservation,
            );
            const newestHasObservation = newestPurchase.transactions.find(
                (transaction) => !!transaction.imageObservation,
            );

            // Caso 1: Solo la más reciente tiene observación - retornar el texto
            if (newestHasObservation) {
                return newestHasObservation.imageObservation;
            }

            // Caso 2: La más antigua tiene observación y la más reciente no ha sido entregada - retornar la observación de la más antigua
            if (
                oldestHasObservation &&
                !newestPurchase.transactions.find(
                    (transaction) => transaction.name === 'Pedido Entregado',
                )
            ) {
                return oldestHasObservation.imageObservation;
            }

            // Caso 3: Solo la más antigua tiene observación - retornar null
            if (oldestHasObservation) {
                return null;
            }

            // Caso 4: Ninguna tiene observación - retornar null
            return null;
        }
        return null;
    };

    // se hace uso del array de deliveries
    // y se hace uso del array de productsDelivered
    // para hacer el conteo de entregas por cada delivery
    const renderOrdersByUser = () => {
        const render = deliveryUsers.map((item, index) => {
            if (productsDelivered) {
                const userDeliveredOrder = productsDelivered.find(
                    (products) => products.userId === item.id,
                );
                if (!!userDeliveredOrder) {
                    return (
                        <span style={{ fontSize: 12 }} key={`user-delivery-${index}`}>
                            <Link
                                to={`${process.env.PUBLIC_URL}/dashboard/${item.franchiseId}/users/${item.id}`}
                                style={{ color: 'darkblue' }}
                            >
                                {item.name.split(' ')[0]}
                            </Link>
                            : <strong>{userDeliveredOrder.quantity}</strong> ({userDeliveredOrder.longway_count})
                            <br />
                        </span>
                    );
                }
                return null;
            }
            return null;
        });
        return render;
    };

    if (!loaded)
        return (
            <div style={{ display: 'flex', height: '50vh' }}>
                <Loader />
            </div>
        );

    const renderedOrdersByUser = renderOrdersByUser();
    const columns = columnsDataTable(pagination.total, auth.user.role);

    const weekStats = quantityDeliveredBottlesByDelivery()

    return (
        <Fragment>
            <div className="container-fluid rents-wrapper">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="d-flex justify-content-between align-items-center pt-2 pb-2">
                            <div>
                                {auth.user.role !== 'DELIVERY' && (
                                    <Link
                                        to={`${process.env.PUBLIC_URL}/recargas/nueva-recarga`}
                                        className="btn btn-primary"
                                    >
                                        Nuevo pedido
                                    </Link>
                                )}
                                &nbsp;&nbsp;
                                {(auth.user.role === 'ADMIN' || auth.user.role === 'FRANCHISE') && (
                                    <button
                                        className="btn btn-warning"
                                        onClick={() => setShowStatitics(!showStatitics)}
                                    >
                                        <i className="fa fa-eye"></i>
                                    </button>
                                )}
                            </div>
                            <div>
                                <button className="btn btn-success" onClick={refreshPage}>
                                    <i className="fa fa-refresh"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    {!!productsDelivered && productsDelivered.length > 0 && (
                        <div className="col-sm-12 d-flex justify-content-between mt-2 mb-2">
                            <span style={{ fontSize: 14 }}>
                                <div>
                                    {mondayWeek.toLocaleDateString('es-VE')} -{' '}
                                    {sundayWeek.toLocaleDateString('es-VE')}
                                </div>
                            </span>
                            <div>
                                <div>
                                    {auth.user.role === 'DELIVERY' && <span style={{ fontSize: 14 }}>Tus entregas: <strong>{weekStats.quantity} ({weekStats.longway})</strong></span>}
                                    {(auth.user.role === 'ADMIN' || auth.user.role === 'FRANCHISE') && <span style={{ fontSize: 14 }}>Total entregas: <strong>{weekStats.quantity} ({weekStats.longway})</strong></span>}
                                </div>
                                {showStatitics && (
                                    <div style={{ textAlign: 'right' }}>{renderedOrdersByUser}</div>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div id="basicScenario" className="order-list">
                                    <ReactTable
                                        columns={columns}
                                        className="-striped "
                                        multiSelectOption={false}
                                        data={orders}
                                        page={pageTable}
                                        pages={totalPages()}
                                        defaultPageSize={pagination.length}
                                        showPageSizeOptions={false}
                                        onPageChange={(index) => handlePage(index + 1)}
                                        manual
                                        nextText="Siguiente"
                                        previousText="Anterior"
                                        getTrProps={(state, rowInfo, column, instance) => ({
                                            onClick: (e) =>
                                                props.history.push(
                                                    `/recargas/detalle/${rowInfo.original.id}`,
                                                ),
                                        })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment >
    );
}

export default Orders;
